import {useEffect, useMemo, useRef, useState} from "react";
import {useAuthState} from "react-firebase-hooks/auth";
import {useLocation, useNavigate} from "@tanstack/react-router";

import RippleButton from "@/components/RippleButton";
import FeedbackDialog from "@/components/FeedbackDialog";
import {auth} from "@/features/api";

import Pig from "@/icons/pig.svg?react";
import Settings from "@/icons/settings.svg?react";
import Trendaro from "@/icons/trendaro.svg?react";
import SignOut from "@/icons/sign-out.svg?react";
import HelpCircle from "@/icons/help-circle.svg?react";
import Menu from "@/icons/menu.svg?react";
import Notifications from "@/icons/notifications.svg?react";
import NotificationsOff from "@/icons/notifications-off.svg?react";

import {useLocalStorage, useMediaQuery} from "@uidotdev/usehooks";
import useSignOut from "@/features/auth/hooks/useSignOut";
import {PushNotificationsContext} from "./push-notifications-context";
import {usePushNotifications} from "@/hooks/usePushNotifications";
import {useRegisterServiceWorker} from "@/hooks/useRegisterServiceWorker";
import GlobalToastRegion from "@/components/GlobalToastRegion";
import {showToast} from "@/components/GlobalToastRegion/toast-queue";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/Popover";
import {ToggleOption} from "@/components/ToggleOption";
import {useCookiesConsent} from "@/features/auth/hooks/useCookiesConsent";
import {useGoogleAnalytics} from "@/features/system/hooks/useGoogleAnalytics";
import {useSetSentryUser} from "@/hooks/useSetSentryUser";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {onProdEnv} from "@/utils/detectors";

const pageTitleMap = {
  "/": "Testování",
  "/flock": "Moje stádo",
  "/settings": "Nastavení",
  "/game": "Hra",
};

export const AppLayout = ({children}: {children: React.ReactNode}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery("(min-width: 1200px)");
  const [isSidebar, setIsSidebar] = useState(isDesktop);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [user, isAuthStateLoading] = useAuthState(auth);
  const userProfile = useGetUserProfile(user?.uid);
  const [, setPreviousPath] = useLocalStorage("previousPath", "");
  const {mutate: signOutMutate} = useSignOut(auth);

  // detect page type
  const isPollPage = location.pathname.includes("/poll");
  const isSettingsPage = location.pathname.includes("/settings");
  const isFlockPage = location.pathname.includes("/flock");

  useRegisterServiceWorker({onRegisterError: console.error});
  const pushNotifications = usePushNotifications();
  useCookiesConsent();
  useGoogleAnalytics();
  useSetSentryUser(user);

  // Route state these effects read when they fire, rather than trigger on.
  // Depending on `location` directly would re-run the redirect after it has
  // navigated, storing "/login" as the previous path and losing the page the
  // user actually came from; the toast effect would likewise re-fire on every
  // query-string change.
  //
  // `setPreviousPath` is held the same way for the same reason: it comes from
  // `useLocalStorage`, whose setter is `useCallback(..., [key, store])`, so
  // writing the path gives the setter a new identity. Depending on it would
  // run the redirect a second time and store "/login".
  const locationRef = useRef(location);
  const pageFlagsRef = useRef({isFlockPage, isSettingsPage});
  const setPreviousPathRef = useRef(setPreviousPath);
  useEffect(() => {
    locationRef.current = location;
    pageFlagsRef.current = {isFlockPage, isSettingsPage};
    setPreviousPathRef.current = setPreviousPath;
  }, [location, isFlockPage, isSettingsPage, setPreviousPath]);

  useEffect(() => {
    // if the user is not logged in, redirect to the login page
    if (!isAuthStateLoading && !user) {
      // save the previous path to redirect the user back after login
      setPreviousPathRef.current(locationRef.current.href);

      navigate({to: "/login"});
    }
  }, [isAuthStateLoading, user, navigate]);

  useEffect(() => {
    if (!isAuthStateLoading && user && userProfile.data?.state === "deleted") {
      navigate({to: "/deleted"});
    }
  }, [isAuthStateLoading, user, userProfile.data?.state, navigate]);

  useEffect(() => {
    setIsSidebar(isDesktop);
  }, [isDesktop]);

  useEffect(() => {
    // skip if user is not logged in
    if (!user) return;

    if (
      // if url contains `?source=old_trendaro`
      (locationRef.current.search as {source?: string}).source ===
      "old_trendaro"
    ) {
      if (pageFlagsRef.current.isFlockPage) {
        showToast(
          "Detail tvého stáda teď už najdeš jen tady, proto jsme tě sem rovnou přesměrovali. Všechna data a účet zůstávají a Trendaro na nové adrese je mnohem rychlejši.",
          "warning",
          16000,
        );
      }

      if (pageFlagsRef.current.isSettingsPage) {
        showToast(
          "Nastavení už najdeš jen tady, proto jsme tě sem rovnou přesměrovali. Všechna data a účet zůstávají a Trendaro na nové adrese je mnohem rychlejši.",
          "warning",
          16000,
        );
      }
    }
  }, [user]);

  const renderSidebar = useMemo(
    () => (
      <aside className="sm:py-g-1 sm:pl-g-1 xl:py-g-2 xl:pl-g-2 absolute z-10 h-full w-full sm:relative sm:w-[400px]">
        <div className="bg-surface-bright p-g-3 h-full rounded-md">
          <div className="gap-g-2 [@media((min-width:600px)_and_(min-height:700px))]:gap-g-3 mx-auto flex h-full max-w-[200px] flex-col">
            <div className="xs:block mx-auto hidden size-[100px] rounded-full bg-[url('/images/pig.jpg')] bg-cover bg-no-repeat [@media(min-height:700px)]:size-[150px]"></div>

            <RippleButton
              onClick={() => {
                navigate({to: "/"});
                if (!isDesktop) setIsSidebar(false);
              }}
              variant="outlined"
              className={`${location.pathname === "/" ? "fill-primary-container text-primary-container border-primary-container" : ""}`}
            >
              <Trendaro className="size-i-2 min-w-i-2" />
              Testování
            </RippleButton>
            <RippleButton
              onClick={() => {
                navigate({to: "/flock"});
                if (!isDesktop) setIsSidebar(false);
              }}
              variant="outlined"
              className={`${location.pathname === "/flock" ? "fill-primary-container text-primary-container border-primary-container" : ""}`}
            >
              <Pig className="size-i-2 min-w-i-2" />
              Moje stádo
            </RippleButton>
            <RippleButton
              onClick={() => {
                navigate({to: "/settings"});
                if (!isDesktop) setIsSidebar(false);
              }}
              variant="outlined"
              className={`${location.pathname === "/settings" ? "fill-primary-container text-primary-container border-primary-container" : ""}`}
            >
              <Settings className="size-i-2 min-w-i-2" />
              Nastavení
            </RippleButton>
            <RippleButton
              variant="outlined"
              className="w-full"
              onClick={() =>
                window.open(
                  "https://www.trendaro.cz/faq",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              <HelpCircle className="size-i-2 min-w-i-2" />
              Časté dotazy
            </RippleButton>

            <div className="grow"></div>

            <RippleButton onClick={() => signOutMutate()} variant="outlined">
              <SignOut className="size-i-2 min-w-i-2" />
              Odhlásit se
            </RippleButton>
            <RippleButton
              variant="text"
              className="mt-g-2 sm:mt-g-3 text-on-surface-variant mx-auto text-sm"
              onClick={() => setIsFeedbackOpen(true)}
              aria-label="Otevřít formulář pro zpětnou vazbu"
            >
              Nějaký problém? Napište nám
            </RippleButton>
          </div>
        </div>
      </aside>
    ),
    [location.pathname, isDesktop, navigate, signOutMutate],
  );

  const renderHeader = useMemo(
    () => (
      <header
        className={[
          "h-ch-3 sm:h-ch-4 min-h-ch-3 sm:min-h-ch-4",
          onProdEnv ? "bg-secondary-container" : "bg-dev-env",
          "gap-g-1 justify-left px-g-2 flex w-full items-center rounded-b-md fill-white text-white sm:px-8",
        ].join(" ")}
      >
        <RippleButton
          onClick={() => setIsSidebar((prev) => !prev)}
          variant="icon"
          className="xs:min-w-14 min-w-12"
        >
          <Menu className="size-8 min-w-8" />
        </RippleButton>

        <div className="ml-g-2 min-w-[120px] text-xl font-semibold">
          {pageTitleMap[location.pathname as keyof typeof pageTitleMap]}
        </div>

        <div className="gap-g-3 xs:gap-8 px-g-2 hidden w-full items-center justify-center sm:flex">
          <div className="relative flex items-center">
            <div
              className={`xs:size-14 xs:-top-3 xs:-left-3 absolute -top-2 -left-2 size-12 rounded-full sm:size-16 ${location.pathname === "/" ? "bg-white/30" : ""}`}
            >
              {" "}
            </div>
            <RippleButton onClick={() => navigate({to: "/"})} variant="icon">
              <Trendaro className="size-8 min-w-8 sm:size-10 sm:min-w-10" />
            </RippleButton>
          </div>

          <div className="relative hidden items-center sm:flex">
            <div
              className={`xs:size-14 xs:-top-3 xs:-left-3 absolute -top-2 -left-2 size-12 rounded-full sm:size-16 ${location.pathname === "/flock" ? "bg-white/30" : ""}`}
            >
              {" "}
            </div>
            <RippleButton
              onClick={() => navigate({to: "/flock"})}
              variant="icon"
            >
              <Pig className="size-8 min-w-8 sm:size-10 sm:min-w-10" />
            </RippleButton>
          </div>
        </div>

        <div className="mr-g-1 md:mr-g-3 relative flex w-full items-center justify-end sm:w-auto">
          <Popover>
            <PopoverTrigger asChild>
              <RippleButton
                variant="icon"
                title="Zobrazovat notifikace v rámci tohoto prohlížeče"
              >
                {pushNotifications.isEnabled ? (
                  <Notifications className="m-1 size-6 min-w-6 sm:size-8 sm:min-w-8" />
                ) : (
                  <NotificationsOff className="m-1 size-6 min-w-6 sm:size-8 sm:min-w-8" />
                )}
              </RippleButton>
            </PopoverTrigger>
            <PopoverContent sideOffset={0} align="center">
              <div className="flex flex-col gap-2 sm:gap-3">
                <div className="flex justify-center font-semibold">
                  <Notifications className="size-i-2 min-w-i-2 mr-g-1" />
                  Push notifikace
                </div>
                <ToggleOption
                  name="push"
                  label="Zobrazovat v rámci tohoto zařízení"
                  checked={pushNotifications.isEnabled}
                  onChange={(e) =>
                    pushNotifications.setIsEnabled(e.target.checked)
                  }
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <img
          className="hidden sm:block"
          src="/images/logo-trendaro-white.svg"
          width="112"
          height="16"
        />
      </header>
    ),
    [location.pathname, pushNotifications, navigate],
  );

  const renderMain = () => {
    return (
      <main
        className={`size-full ${isPollPage ? "" : "overflow-y-auto rounded-md"}`}
      >
        <PushNotificationsContext.Provider value={pushNotifications}>
          {children}
        </PushNotificationsContext.Provider>
      </main>
    );
  };

  const shouldBlockAppContent =
    !!user && (userProfile.isLoading || userProfile.data?.state === "deleted");

  return (
    <div className="h-dvh max-h-dvh w-full">
      {!shouldBlockAppContent &&
        !isAuthStateLoading &&
        user &&
        !isPollPage &&
        renderHeader}
      <div
        className={[
          "relative w-full",
          isPollPage
            ? "h-full"
            : "flex h-[calc(100dvh-var(--spacing-ch-3))] sm:h-[calc(100dvh-var(--spacing-ch-4))]",
        ].join(" ")}
      >
        {!shouldBlockAppContent &&
          !isAuthStateLoading &&
          user &&
          !isPollPage &&
          isSidebar &&
          renderSidebar}
        {!shouldBlockAppContent && renderMain()}
      </div>

      <FeedbackDialog
        context="menu"
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
      />

      <GlobalToastRegion />
    </div>
  );
};
